/**
 * Review's vocabulary: which views exist, which states each holds, and how a row
 * describes itself.
 *
 * Free of React and of api.ts on purpose, like navigation.ts: the words a customer reads
 * for a state are a product decision, and keeping them in a module the architecture tests
 * can import is what stops a second, slightly different label appearing somewhere else.
 *
 * The STATES are the server's (GET /review/items), never invented here, and every row
 * still carries exactly one:
 *
 * - needs_review — a person must correct or decide something: a conflict, an ambiguity,
 *   missing identity, a safety condition, an unread card, a card whose plan expired.
 * - ready        — nothing is wrong; it waits only for a person's approval.
 * - waiting      — approved, not yet in the CRM: queued, held by a write gate (CRM sending
 *                  off, the daily limit), or running. Nothing failed.
 * - sent         — the CRM consequence actually completed. Never on approval alone.
 * - failed       — approved, but the CRM did not complete the write, and a retry may. A
 *                  send stopped by something a person must change is needs_review instead.
 * - rejected     — closed with no CRM change: rejected, a discarded card, or "nothing to
 *                  do". The row says which.
 * - processing   — still being read; nothing to decide yet.
 *
 * A customer chooses between four VIEWS, each a fixed set of those states. Seven chips
 * asked people to learn the lifecycle; four ask only "what is waiting for me, and what
 * came of the rest":
 *
 * - Needs review = needs_review + ready + failed — everything waiting on a PERSON: a thing
 *   to fix, a thing to approve, and a send that did not complete and can be retried.
 * - Sent         = sent only.
 * - Not sent     = rejected (and discarded, and no action needed) + waiting — closed
 *   without reaching the CRM, or approved and waiting on a write gate nobody here can
 *   open. Neither is a person's work.
 * - All          = everything, including rows still being read.
 *
 * The server applies the same mapping (`VIEW_STATES` in @signal/db) and counts each view;
 * the nav badge is the Needs review count across every source.
 *
 * A batch (a CSV import or several card photos) appears under every view it has contacts
 * in, and its summary says how many of each.
 */

import type { ReviewView } from './navigation.js';

export type ReviewStateKey =
  'needs_review' | 'ready' | 'waiting' | 'sent' | 'failed' | 'rejected' | 'processing';
export type { ReviewView };

/** The four views, in the order they are offered. */
export const VIEWS: readonly ReviewView[] = ['needs_review', 'sent', 'not_sent', 'all'];

/** What a view's chip is called. */
export const VIEW_LABEL: Record<ReviewView, string> = {
  needs_review: 'Needs review',
  sent: 'Sent',
  not_sent: 'Not sent',
  all: 'All',
};

/** The states each view holds. `all` holds every state, `processing` included. */
export const VIEW_STATES: Record<Exclude<ReviewView, 'all'>, readonly ReviewStateKey[]> = {
  needs_review: ['needs_review', 'ready', 'failed'],
  sent: ['sent'],
  not_sent: ['rejected', 'waiting'],
};

/** The view a row's state belongs to (besides All). Null for a row still being read. */
export function viewOf(state: ReviewStateKey): Exclude<ReviewView, 'all'> | null {
  for (const v of ['needs_review', 'sent', 'not_sent'] as const) {
    if (VIEW_STATES[v].includes(state)) return v;
  }
  return null;
}

/**
 * What a row says about where it is. Text, always — never a colour alone — and written
 * as what a person would say, not as the lifecycle's name for it.
 */
export const STATE_LABEL: Record<ReviewStateKey, string> = {
  needs_review: 'Needs review',
  ready: 'Ready to send',
  waiting: 'Approved — waiting to send',
  sent: 'Sent',
  failed: 'Couldn’t send — try again',
  rejected: 'Rejected',
  processing: 'Still reading',
};

/** Where a row came from, as a word. Source is a filter, not a section. */
export const SOURCE_LABEL: Record<'email' | 'business_card' | 'import' | 'web_page', string> = {
  email: 'Email',
  business_card: 'Business Card',
  import: 'Import',
  web_page: 'Web page',
};

/**
 * A row's status text.
 *
 * Closed rows say HOW they closed: a person rejected it, a card was discarded, or there
 * was nothing to do. Three different facts that would otherwise share one word.
 */
export function rowStatus(row: { kind: string; state: ReviewStateKey; status?: string; outcome?: string | null }): string {
  if (row.state !== 'rejected') return STATE_LABEL[row.state];
  if (row.kind === 'card' && row.outcome === 'cancelled') return 'Discarded';
  if (row.status === 'skipped' || row.outcome === 'no_action') return 'No action needed';
  return 'Rejected';
}

/** The fallback reason: true of every row that needs a person, and says what to do. */
export const CHECK_BEFORE_SENDING = 'Check the contact before sending';

/**
 * An email's review reasons, as the one short thing to do about it. Only the reasons a
 * row can usefully lead with; the detail lists every reason in full.
 */
const EMAIL_REASON: Record<string, string> = {
  enrichment_conflict: 'Details differ from your CRM',
  identity_ambiguous: 'More than one Keap contact matches — choose who to send',
  role_mailbox: 'This email may be shared — confirm the person',
  shared_address_in_use: 'More than one Keap contact uses this email — choose the person',
  no_contact: 'Someone couldn’t be matched — check who to include',
  marker_stage_conflict: 'Two labels disagree — choose a tag',
  crm_stage_conflict: 'Your CRM has a different tag for this contact',
  missing_identity: 'No email address to match',
  no_eligible_contact: 'Choose who to include',
  identity_unresolved: 'Your CRM couldn’t be checked',
  stale_plan: 'Changed — check it again',
  injection_suspected: 'Unusual instructions in the email — check it',
  contact_type_required: 'Choose a Contact Type for new contacts',
  // Write gates: nothing failed, nothing was attempted for the held part.
  global_disabled: 'CRM sending is currently off',
  workspace_not_enabled: 'CRM sending is currently off',
  crm_write_disabled: 'CRM sending is currently off',
  pre_activation: 'Approved before CRM sending was on — send it again',
  rollout_limit_reached: 'Today’s CRM limit was reached — it can be sent tomorrow',
};

/** Why a card row is where it is, in a phrase. Null when the state says enough. */
export function cardContext(outcome: string | null): string | null {
  if (!outcome || outcome === 'cancelled') return null;
  if (outcome === 'unreadable') return 'The card could not be read';
  if (outcome === 'plan_expired') return 'Check it again before saving';
  if (outcome === 'crm_write_disabled') return 'CRM sending is currently off';
  // Refused by a gate that has since opened: nothing was sent then, and saving again is a new decision.
  if (outcome === 'held_earlier') return 'Not sent — CRM sending was off then. Check it and save again';
  return 'The CRM write did not complete';
}

/**
 * The one short, actionable reason a row shows beside its status — only where it adds
 * something. A row waiting on a person always has one; a sent or closed row has none.
 */
export function rowReason(row: {
  kind: string; state: ReviewStateKey; reasons?: readonly string[]; outcome?: string | null;
  delivery?: { sent: number; tried: number };
}): string | null {
  if (row.kind === 'batch') return null;
  if (row.kind === 'card' && ['needs_review', 'failed', 'waiting', 'ready'].includes(row.state)) {
    return cardContext(row.outcome ?? null) ?? (row.state === 'needs_review' ? CHECK_BEFORE_SENDING : null);
  }
  if (!['needs_review', 'waiting', 'failed'].includes(row.state)) return null;
  // Some people of a conversation reached the CRM and some did not: said, never hidden.
  const d = row.delivery;
  const partly = d && d.sent > 0 && d.sent < d.tried ? `Sent for ${d.sent} of ${d.tried} people` : null;
  const first = (row.reasons ?? []).map((r) => EMAIL_REASON[r]).find(Boolean)
    ?? (row.state === 'needs_review' ? CHECK_BEFORE_SENDING : null);
  return [partly, first].filter(Boolean).join(' · ') || null;
}

/** "23 ready · 2 need review" — a batch's contacts by state, largest decisions first. */
export function batchSummary(counts: Partial<Record<ReviewStateKey, number>>): string {
  const parts: string[] = [];
  const n = (k: ReviewStateKey) => counts[k] ?? 0;
  if (n('ready') > 0) parts.push(`${n('ready')} ready`);
  if (n('needs_review') > 0) parts.push(`${n('needs_review')} need${n('needs_review') === 1 ? 's' : ''} review`);
  if (n('failed') > 0) parts.push(`${n('failed')} couldn’t send`);
  if (n('waiting') > 0) parts.push(`${n('waiting')} waiting to send`);
  if (n('processing') > 0) parts.push(`${n('processing')} still reading`);
  if (n('sent') > 0) parts.push(`${n('sent')} sent`);
  if (n('rejected') > 0) parts.push(`${n('rejected')} closed`);
  return parts.join(' · ') || 'Nothing to decide';
}

/** "30 contacts · 23 ready · 2 need review": the whole line a batch row shows. */
export function batchLine(total: number, counts: Partial<Record<ReviewStateKey, number>>): string {
  return `${total} contact${total === 1 ? '' : 's'} · ${batchSummary(counts)}`;
}

/** The one empty state for a view. Specific, short, and never two side by side. */
export function emptyCopy(view: ReviewView, source: string, totalForSource: number): string {
  if (source === 'email' && totalForSource === 0) {
    return 'Apply one of your capture labels in Gmail or a category in Outlook.';
  }
  switch (view) {
    case 'needs_review': return 'Nothing needs your attention.';
    case 'sent': return 'Nothing has been sent to your CRM yet.';
    case 'not_sent': return 'Nothing has been rejected, discarded or held back.';
    default: return 'Nothing captured yet. Add contacts from Capture, or mark an email.';
  }
}
